import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { topicMetadataDisplayTopic$key } from "scmp-app/queries/__generated__/topicMetadataDisplayTopic.graphql";

import {
  AdditionalMetadataContainer,
  AdditionalMetadataItem,
  AdditionalMetadataPropertyItem,
  AdditionalMetadataPropertyItemContainer,
  AdditionalMetadataPropertyType,
  Container,
  Divider,
  ExtraMetadataContainer,
  ExtraMetadataItem,
  ImageIcon,
  LogoIcon,
  MetadataDescription,
  MetadataRow,
  MetadataTitle,
  StyledBaseLink,
  StyledEntityLink,
} from "./styles";

type Props = {
  className?: string;
  reference: topicMetadataDisplayTopic$key;
};

export const TopicMetadataDisplay: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const topic = useFragment(
    graphql`
      fragment topicMetadataDisplayTopic on Topic {
        ...hooksTopic
        extraMetadata {
          key
          value
          topic {
            name
            ...entityLink
            types
            urlAlias
            mobileImage: image(type: MOBILE_APP_IMAGE) {
              style(filter: { style: "300x300" }) {
                url
              }
            }
          }
        }
        additionalMetadata {
          propertyType
          logo {
            title
            url
          }
          properties {
            key
            value
          }
        }
      }
    `,
    reference_,
  );

  return (
    <Container className={className}>
      {topic.extraMetadata && topic.extraMetadata.length > 0 && (
        <>
          <Divider />
          <ExtraMetadataContainer>
            {topic.extraMetadata.map(
              (item, index) =>
                item?.key &&
                item?.value && (
                  <ExtraMetadataItem key={index}>
                    <MetadataRow>
                      <MetadataTitle>{item.key}</MetadataTitle>
                      <MetadataDescription>
                        {item.value.split(";").map((line, index_) => (
                          <span key={index_}>
                            {index_ > 0 && <br />}
                            {item.topic?.urlAlias ? (
                              <StyledBaseLink pathname={item.topic.urlAlias}>{line}</StyledBaseLink>
                            ) : (
                              line
                            )}
                          </span>
                        ))}
                      </MetadataDescription>
                    </MetadataRow>
                    {item.topic?.types?.includes("person") &&
                      item.topic?.mobileImage?.style?.url && (
                        <StyledEntityLink reference={item.topic}>
                          <ImageIcon
                            $aspectRatio="1"
                            alt={item.topic?.name}
                            lazyload={false}
                            src={item.topic?.mobileImage.style.url}
                          />
                        </StyledEntityLink>
                      )}
                  </ExtraMetadataItem>
                ),
            )}
          </ExtraMetadataContainer>
          {topic.additionalMetadata && topic.additionalMetadata.length > 0 && (
            <>
              <Divider />
              <AdditionalMetadataContainer>
                {topic.additionalMetadata?.map(item => (
                  <AdditionalMetadataItem key={item?.propertyType}>
                    <MetadataRow>
                      {item?.propertyType && (
                        <AdditionalMetadataPropertyType>
                          {item.propertyType.replaceAll(/[-_]/g, " ")}:
                        </AdditionalMetadataPropertyType>
                      )}
                      {item?.logo && (
                        <LogoIcon
                          $aspectRatio="1"
                          alt={item.logo.title ?? ""}
                          lazyload={false}
                          src={item.logo.url ?? ""}
                        />
                      )}
                    </MetadataRow>
                    <AdditionalMetadataPropertyItemContainer>
                      {item?.properties?.map(
                        property =>
                          property?.key &&
                          property?.value && (
                            <AdditionalMetadataPropertyItem
                              key={`${item?.propertyType}-${property?.key}`}
                            >
                              <MetadataRow>
                                <MetadataTitle>{property.key}</MetadataTitle>
                                <MetadataDescription
                                  dangerouslySetInnerHTML={{ __html: property.value }}
                                />
                              </MetadataRow>
                            </AdditionalMetadataPropertyItem>
                          ),
                      )}
                    </AdditionalMetadataPropertyItemContainer>
                  </AdditionalMetadataItem>
                ))}
              </AdditionalMetadataContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

TopicMetadataDisplay.displayName = "TopicMetadataDisplay";
